import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "./HeroSlider.css";

const slides = [
    {
        landscape: "asset/images/slide-01.png",
        portrait:  "asset/images/slide-01-potraite.png",
        label:     "Women's Collection",
        title:     "NEW SEASON",
        labelEffect: "fadeInDown",
        titleEffect: "fadeInUp",
        btnEffect:   "zoomIn",
    },
    {
        landscape: "asset/images/slide-02.png",
        portrait:  "asset/images/slide-02-potraite.png",
        label:     "Men's Collection",
        title:     "Fresh Arrivals",
        labelEffect: "rollIn",
        titleEffect: "lightSpeedIn",
        btnEffect:   "slideInUp",
    },
    {
        landscape: "asset/images/slide-03.png",
        portrait:  "asset/images/slide-03-potraite.png",
        label:     "Bridal & Occasion Wear",
        title:     "Dress to Impress",
        labelEffect: "rotateInDownLeft",
        titleEffect: "rotateInUpRight",
        btnEffect:   "rotateIn",
    },
    {
        landscape: "asset/images/slide-04.png",
        portrait:  "asset/images/slide-04-potraite.png",
        label:     "Kids & Teen Fashion",
        title:     "Style for Every Age",
        labelEffect: "rotateInDownLeft",
        titleEffect: "rotateInUpRight",
        btnEffect:   "rotateIn",
    },
];

interface HeroSliderProps {
    headerHeight?: number;
}

const HeroSlider: React.FC<HeroSliderProps> = ({ headerHeight = 0 }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile on mount and on resize
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth <= 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // Layer animations (unchanged from original)
    useEffect(() => {
        const layers = document.querySelectorAll(".layer-slick1");
        layers.forEach((layer: any) => {
            const delay  = layer.dataset.delay || 0;
            const effect = layer.dataset.appear || "fadeIn";
            setTimeout(() => {
                layer.classList.add(effect);
                layer.classList.remove("visible-false");
            }, delay);
        });
    });

    const settings = {
        dots: true,
        infinite: true,
        autoplay: true,
        autoplaySpeed: 5000,
        speed: 700,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
        pauseOnHover: false,
        fade: true,
    };

    return (
        <>
            <style>{`
                .section-slide {
                    width: 100%;
                    overflow: hidden;
                }

                /* ── DESKTOP: subtract header height from 100vh so the slider
                      fits exactly within the visible viewport ── */
                @media (min-width: 769px) {
                    .section-slide .slick1,
                    .section-slide .slick-list,
                    .section-slide .slick-track,
                    .section-slide .slick-slide,
                    .section-slide .slick-slide > div {
                        height: calc(100vh - var(--hero-offset, 0px));
                        min-height: 560px;
                    }
                }

                /* ── MOBILE: let slick wrappers auto-size to the portrait image ── */
                @media (max-width: 768px) {
                    .section-slide .slick1,
                    .section-slide .slick-list,
                    .section-slide .slick-track,
                    .section-slide .slick-slide,
                    .section-slide .slick-slide > div {
                        height: auto !important;
                        min-height: unset !important;
                    }
                }

                /* ── Desktop slide item ── */
                .item-slick1 {
                    position: relative;
                    width: 100%;
                    height: calc(100vh - var(--hero-offset, 0px));
                    min-height: 560px;
                    background-size: cover;
                    background-position: center center;
                    background-repeat: no-repeat;
                    background-color: #1a1a1a;
                    display: flex;
                    align-items: center;
                }

                /* ── Mobile slide item ── */
                .item-slick1-mobile {
                    position: relative;
                    width: 100%;
                    display: block;
                    background-color: #1a1a1a;
                }

                /*
                 * Portrait image: width 100%, height auto
                 * → container grows to the natural image height
                 * → full image always visible, no cropping
                 */
                .item-slick1-mobile .slide-img {
                    display: block;
                    width: 100%;
                    height: auto;
                }

                /* Gradient overlay for text readability */
                .item-slick1-mobile .slide-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        to bottom,
                        rgba(0,0,0,0.05) 0%,
                        rgba(0,0,0,0.35) 55%,
                        rgba(0,0,0,0.55) 100%
                    );
                    z-index: 1;
                }

                /* Text pinned to the bottom of the portrait image */
                .item-slick1-mobile .slide-content {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    z-index: 2;
                    padding: 24px 20px 44px;
                }

                /* ── Slick dots ── */
                .section-slide .slick-dots {
                    bottom: 16px;
                    z-index: 10;
                }
                .section-slide .slick-dots li button:before {
                    color: #fff;
                    opacity: 0.6;
                    font-size: 8px;
                }
                .section-slide .slick-dots li.slick-active button:before {
                    color: #fff;
                    opacity: 1;
                }
            `}</style>

            <section
                className="section-slide"
                style={{
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "100vw",
                    // Inject the header height as a CSS variable so the
                    // calc() expressions above can reference it at runtime.
                    "--hero-offset": `${headerHeight}px`,
                } as React.CSSProperties}
            >
                <Slider {...settings} className="slick1">
                    {slides.map((slide, index) =>
                        isMobile ? (
                            /* ── MOBILE: portrait <img>, full height, no cropping ── */
                            <div key={index}>
                                <div className="item-slick1-mobile">
                                    <img
                                        src={slide.portrait}
                                        alt={slide.title}
                                        className="slide-img"
                                    />
                                    <div className="slide-overlay" />
                                    <div className="slide-content">
                                        <div
                                            className="layer-slick1 animated visible-false"
                                            data-appear={slide.labelEffect}
                                            data-delay="0"
                                        >
                                            <span className="ltext-101 cl2 respon2">
                                                {slide.label}
                                            </span>
                                        </div>
                                        <div
                                            className="layer-slick1 animated visible-false"
                                            data-appear={slide.titleEffect}
                                            data-delay="800"
                                        >
                                            <h2 className="ltext-201 cl2 p-t-19 p-b-43 respon1">
                                                {slide.title}
                                            </h2>
                                        </div>
                                        <div
                                            className="layer-slick1 animated visible-false"
                                            data-appear={slide.btnEffect}
                                            data-delay="1600"
                                        >
                                            <button
                                                className="flex-c-m stext-101 cl0 size-101 bg1 bor1 hov-btn1 p-lr-15 trans-04"
                                                onClick={() => navigate("/shop")}
                                                style={{ border: "none", cursor: "pointer" }}
                                            >
                                                Shop Now
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* ── DESKTOP: landscape background-image, full viewport height minus header ── */
                            <div key={index}>
                                <div
                                    className="item-slick1"
                                    style={{ backgroundImage: `url('${slide.landscape}')` }}
                                >
                                    <div className="container h-full">
                                        <div className="flex-col-l-m h-full p-t-100 p-b-30 respon5">
                                            <div
                                                className="layer-slick1 animated visible-false"
                                                data-appear={slide.labelEffect}
                                                data-delay="0"
                                            >
                                                <span className="ltext-101 cl2 respon2">
                                                    {slide.label}
                                                </span>
                                            </div>
                                            <div
                                                className="layer-slick1 animated visible-false"
                                                data-appear={slide.titleEffect}
                                                data-delay="800"
                                            >
                                                <h2 className="ltext-201 cl2 p-t-19 p-b-43 respon1">
                                                    {slide.title}
                                                </h2>
                                            </div>
                                            <div
                                                className="layer-slick1 animated visible-false"
                                                data-appear={slide.btnEffect}
                                                data-delay="1600"
                                            >
                                                <button
                                                    className="flex-c-m stext-101 cl0 size-101 bg1 bor1 hov-btn1 p-lr-15 trans-04"
                                                    onClick={() => navigate("/shop")}
                                                    style={{ border: "none", cursor: "pointer" }}
                                                >
                                                    Shop Now
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    )}
                </Slider>
            </section>
        </>
    );
};

export default HeroSlider;